import React, { useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { useSelector } from "react-redux";
import { getLocationHierarchy } from "../../global/utility/widgetUtils";
import { toMin } from "../../global/utility/statisticsUtil";
import {
  CHART_FONT_FAMILY,
  TOOLTIP_STYLE,
  chartTextStyle,
  buildChartTitle,
  buildLegend,
} from "./ApacheChartUtils";

const ApacheBarChart = ({
  title = "",
  yAxisData = {},
  series = [],
  height = "400px",
  loading = false,
}) => {
  const chartRef = useRef(null);
  const siteTagList = useSelector((state) => state.sitesReducer.siteTagList);
  const option = useMemo(() => {
    const locationIds = yAxisData?.locationIdData ?? [];
    const hasLocationIds = locationIds.some(Boolean);
    const yDataLocationHierarchy = (yAxisData?.data ?? []).map(
      (name, i) => (locationIds[i] && getLocationHierarchy(siteTagList, locationIds[i])) || name
    );

    return {
      textStyle: chartTextStyle,
      title: buildChartTitle(title),
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "none" },
        textStyle: { ...chartTextStyle, color: "#000000" },
        formatter: (params) => {
          const label = params[0]?.axisValue;
          const isMinutes = yAxisData?.toolTipUnit === " minute(s)";
          const formatValue = (val) =>
            isMinutes ? toMin(val) : `${val}${yAxisData?.toolTipUnit ? ` ${yAxisData.toolTipUnit}` : ""}`;
          const rows = params
            .map((p) => `<div style="${TOOLTIP_STYLE}">${p.marker}${p.seriesName}: <b>${formatValue(p.value)}</b></div>`)
            .join("");
          return `<div style="${TOOLTIP_STYLE}">${label}</div>${rows}`;
        },
      },
      legend: buildLegend(true),
      grid: {
        left: 0,
        right: 0,
        bottom: "15%",
        top: title ? 60 : 40,
        containLabel: true,
      },
      xAxis: {
        type: "value",
        minInterval: 1,
        name: yAxisData?.xName || "",
        nameLocation: "middle",
        nameGap: 25,
        nameTextStyle: { fontWeight: "bold" },
        splitLine: { show: false },
        axisLabel: { show: false }
      },
      yAxis: {
        type: "category",
        data: yAxisData?.data,
        name: yAxisData?.yName || "",
        nameLocation: "middle",
        nameGap: 40,
        nameRotate: 90,
        nameTextStyle: { fontWeight: "bold" },
        axisLabel: {
          rotate: 0,
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
        }, ...(hasLocationIds && {
          triggerEvent: true,
          tooltip: {
            show: true,
            textStyle: { ...chartTextStyle, color: "#000000" },
            formatter: (params) =>
              `<div style="${TOOLTIP_STYLE}">${yDataLocationHierarchy[params?.tickIndex] ?? params?.data ?? ""}</div>`,
          },
        }),
      },
      series: series.map((s) => ({
        name: s?.name,
        type: "bar",
        data: s?.data,
        color: s?.color ?? "#0089EC",
        barMaxWidth: 20,
        itemStyle: { borderRadius: [0, 4, 4, 0] },
        emphasis: { focus: "series" },
        label: {
          show: s?.showLabel ?? false,
          position: "right",
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
          color: "#000000",
        },
      })),
    };
  }, [title, yAxisData, series, siteTagList]);

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ height }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
};

export default ApacheBarChart;
